import { useEffect } from "react";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import { useMatchLogStore } from "../../../store/useMatchLogStore";
import type {
  ActionStep,
  LoggerHarness,
  Match,
  QueuedEventSummary,
} from "../types";

interface UseCockpitHarnessParams {
  enabled: boolean;
  match: Match | null;
  resetFlow: () => void;
  setSelectedTeam: (team: "home" | "away") => void;
  currentStepRef: React.MutableRefObject<ActionStep>;
  sendPassEvent: (options: {
    team: "home" | "away";
    passerId: string;
    recipientId: string;
  }) => void;
  sendRawEvent: (payload: Record<string, any>) => void;
  undoLastEvent: () => Promise<void> | void;
  computedDriftSeconds: number;
  forcedDriftSeconds: number | null;
  driftSeconds: number;
  showDriftNudge: boolean;
}

export const useCockpitHarness = ({
  enabled,
  match,
  resetFlow,
  setSelectedTeam,
  currentStepRef,
  sendPassEvent,
  sendRawEvent,
  undoLastEvent,
  computedDriftSeconds,
  forcedDriftSeconds,
  driftSeconds,
  showDriftNudge,
}: UseCockpitHarnessParams): void => {
  useEffect(() => {
    if (!enabled || !match) return;

    const harness: LoggerHarness = {
      resetFlow,
      setSelectedTeam,
      getCurrentStep: () => currentStepRef.current,
      sendPassEvent,
      sendRawEvent,
      getMatchContext: () => ({
        matchId: match.id,
        homeTeamId: match.home_team.id,
        awayTeamId: match.away_team.id,
      }),
      undoLastEvent: () => undoLastEvent(),
      getQueueSnapshot: () => {
        const summarize = (events: MatchEvent[]): QueuedEventSummary[] =>
          events.map((event) => ({
            match_id: event.match_id,
            timestamp: event.timestamp,
            client_id: event.client_id,
            type: event.type,
          }));
        const state = useMatchLogStore.getState();
        return {
          currentMatchId: state.currentMatchId,
          queuedEvents: summarize(state.queuedEvents),
          queuedEventsByMatch: Object.fromEntries(
            Object.entries(state.queuedEventsByMatch).map(
              ([matchKey, events]) => [matchKey, summarize(events)],
            ),
          ),
        };
      },
      clearQueue: () => {
        useMatchLogStore.getState().clearQueuedEvents();
      },
      getDriftSnapshot: () => ({
        computed: computedDriftSeconds,
        forced: forcedDriftSeconds,
        effective: driftSeconds,
        show: showDriftNudge,
      }),
    };

    window.__BASKET_PROMATCH_LOGGER_HARNESS__ = harness;

    return () => {
      delete window.__BASKET_PROMATCH_LOGGER_HARNESS__;
    };
  }, [
    enabled,
    match,
    resetFlow,
    setSelectedTeam,
    currentStepRef,
    sendPassEvent,
    sendRawEvent,
    undoLastEvent,
    computedDriftSeconds,
    forcedDriftSeconds,
    driftSeconds,
    showDriftNudge,
  ]);
};
